import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import type { AsyncSignalValue } from '@component/async.component';
import { FormWorker } from '@feature/dynamic_form/form_manager/form_worker';
import type { Form as FormType } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Basket } from '../../hooks/use-basket';
import { useBasket } from '../../hooks/use-basket';
import { CreateNewMessageAnalyticActions, useCreateNewMessage } from '../../hooks/use-create-new-message';

interface Options {
	isAccessDecisionReady: boolean;
}

export function useScreenForms({ isAccessDecisionReady }: Options) {
	const createNewMessageController = useCreateNewMessage();
	const { reasonForVisitId, basket, formManager } = useBasket();
	const analyticService = useResolve(AnalyticService);

	const [forms, setForms] = useState<FormType[] | null>(null);
	const [formWorker, setFormWorker] = useState<AsyncSignalValue<Readonly<FormWorker>>>({
		flowState: 'pending',
		value: null,
	});

	// Mirrors the previous controller's private fields. Kept as refs so they
	// don't trigger re-renders and stay in sync with callbacks created on mount.
	const basketRef = useRef<Nullable<Basket>>(null);
	const tempDisposersRef = useRef<(() => void)[]>([]);
	const formsRef = useRef<FormType[] | null>(null);

	// Show the access step whenever the basket has participants the user can manage —
	// covers both the "representing another patient" flow and the child-direct flow
	// where legal guardians are surfaced as participants.
	const shouldShowAccessBeforeConclusion = (basket?.Participants.length ?? 0) > 0;
	const shouldShowRef = useRef(shouldShowAccessBeforeConclusion);
	useEffect(() => {
		shouldShowRef.current = shouldShowAccessBeforeConclusion;
	}, [shouldShowAccessBeforeConclusion]);

	const getScreenAfterForms = useCallback((): 'access' | 'conclusion' => {
		return shouldShowRef.current ? 'access' : 'conclusion';
	}, []);

	const allowedToBeHere = useCallback(
		(currentBasket: Nullable<Basket>): boolean => {
			if (!currentBasket) {
				return false;
			}

			const flowData = createNewMessageController.flowData;
			if (flowData.flowState !== 'accessible') {
				return false;
			}

			const selectedReasonForVisit = flowData.value.reasonForVisits.find((rfv) => rfv.Id === reasonForVisitId) ?? null;

			return !(flowData.value.reasonForVisits.length > 0 && !selectedReasonForVisit);
		},
		[reasonForVisitId, createNewMessageController],
	);

	useEffect(
		function whenMount() {
			analyticService.trackPageView({
				customTitle: 'Create new message: Forms',
			});
		},
		[analyticService],
	);

	useEffect(
		function whenBasketIsReady() {
			const currentBasket = basket;
			if (!currentBasket) {
				return;
			}

			basketRef.current = currentBasket;

			if (!allowedToBeHere(currentBasket)) {
				analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.NotAllowedToBeHere, 'Forms');
				createNewMessageController.goToScreen('start');
				return;
			}

			const formsValue = currentBasket.Forms ?? [];
			formsRef.current = formsValue;
			setForms(formsValue);

			if (!formsValue.length) {
				if (!isAccessDecisionReady) {
					return;
				}

				analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.NoForms);
				createNewMessageController.goToScreen(getScreenAfterForms(), { replace: true });
			} else {
				analyticService.trackEvent(
					AnalyticCategories.CreateNewMessage,
					CreateNewMessageAnalyticActions.NumberOfForms,
					`${formsValue.length}`,
				);
			}
		},
		[allowedToBeHere, analyticService, basket, createNewMessageController, getScreenAfterForms, isAccessDecisionReady],
	);

	useEffect(function whenMount() {
		return function whenDemount() {
			tempDisposersRef.current.forEach((dispose) => dispose());
			tempDisposersRef.current = [];
		};
	}, []);

	const getNextFormId = useCallback((currentFormId: number): number | null => {
		const formsValue = formsRef.current;
		if (!formsValue) {
			throw new Error('Forms not loaded...');
		}

		const currentFormIndex = formsValue.findIndex((form) => form.Id === currentFormId);
		return formsValue[currentFormIndex + 1]?.Id ?? null;
	}, []);

	const loadSingleForm = useCallback(
		async (formId: number): Promise<void> => {
			analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.LoadForm, `${formId}`);

			if (basketRef.current == null) {
				setFormWorker({ flowState: 'error', value: null });
				return;
			}

			setFormWorker({ flowState: 'pending', value: null });

			const nextFormWorker = formManager.getFormByFormId(formId);
			if (nextFormWorker == null) {
				setFormWorker({ flowState: 'error', value: null });
				return;
			}

			tempDisposersRef.current.forEach((dispose) => dispose());
			tempDisposersRef.current = [
				nextFormWorker.events.on('onCommit', () => {
					const nextFormId = getNextFormId(nextFormWorker.formId);
					if (nextFormId != null) {
						createNewMessageController.goToScreen('forms', { formId: nextFormId });
					} else {
						createNewMessageController.goToScreen(getScreenAfterForms());
					}
				}),
				nextFormWorker.events.on('onChangePage', (data) => {
					createNewMessageController.goToScreen('forms', {
						formId: nextFormWorker.formId,
						formPage: data.newPagePosition,
					});
				}),
			];

			setFormWorker({ flowState: 'accessible', value: nextFormWorker });
		},
		[analyticService, formManager, createNewMessageController, getNextFormId, getScreenAfterForms],
	);

	return { forms, formWorker, loadSingleForm };
}
